import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimCreateWorkOrderMutation } from 'gql/graphql';
import { ProjectContractTypeEnum, ProjectMethodologyEnum } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import type { TActionCommonInterface } from 'utils/types';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useSetIsWorkOrderChangeRequired } from '../useSetIsWorkOrderChangeRequired';

import { createBimOrderQueriesGql } from './createBimOrderQueries';

export const useCreateBimOrder = ({ projectId }: { projectId?: string }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { projectData, refreshProject } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const hasFilledAddresses = projectData?.contractNavigation?.data?.contractNavData?.metadata?.hasFilledAddresses;
    const hasFilledMandatoryProperties =
        projectData?.contractNavigation?.data?.contractNavData?.metadata?.hasFilledMandatoryProperties;

    const createBimOrderVariables = useMemo(() => ({ contractId: projectData?.contractId }), [projectData?.contractId]);

    const isFrameContract = useMemo(
        () => projectData?.contractType === ProjectContractTypeEnum.Frame,
        [projectData?.contractType],
    );

    const isOrderCreateDisabled = useMemo(
        () =>
            !isFrameContract &&
            !!projectData?.contractNavigation?.data?.contractNavItems?.find((obj) => obj.name === 'Расчет по договору'),
        [isFrameContract, projectData?.contractNavigation?.data?.contractNavItems],
    );

    const { orderCreateBtnTitle, orderCreateTitle, requestId } = useMemo(
        () => ({
            orderCreateBtnTitle: isFrameContract ? 'Создать Наряд-Заказ' : 'Создать Расчет по Договору',
            orderCreateTitle: isFrameContract ? 'Создание Наряд-Заказа' : 'Создание Расчета по Договору',
            requestId: isFrameContract ? 'createBimFrameContractOrder' : 'createBimContractOrder',
        }),
        [isFrameContract],
    );

    const [resetData] = useResetStoreData({
        requestId,
    });

    const analyticsData = useMemo(
        () => ({
            projectId,
            contractId: projectData?.contractId,
            methodology: ProjectMethodologyEnum.Bim,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
        }),
        [
            projectData?.contractId,
            projectData?.contractType,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
        ],
    );

    const successCreateBimOrder = useCallback(
        (data: TActionCommonInterface<BimCreateWorkOrderMutation>) => {
            const { bimCreateWorkOrder } = data?.payload ?? {};

            sendGpnAnalyticsEvent({
                componentId: 'rim_order file_saved',
                constaeventtype: 'undefined',
                component: 'OTHER',
                orderId: bimCreateWorkOrder?.data?.id,
                orderStatus: bimCreateWorkOrder?.data?.status,
                ...analyticsData,
            });
        },
        [analyticsData, sendGpnAnalyticsEvent],
    );

    const [createBimOrderData, createBimOrderStatus, , createBimOrderRequest] = useRequest({
        requestId,
        query: createBimOrderQueriesGql,
        variables: createBimOrderVariables,
        withEffect: false,
        dependenciesUpdater: successCreateBimOrder,
    });

    useSetIsWorkOrderChangeRequired({
        useData: createBimOrderData?.bimCreateWorkOrder?.data,
        status: createBimOrderStatus,
    });

    const actionFn = useCallback(() => {
        if (!hasFilledAddresses || !hasFilledMandatoryProperties) {
            navigate(`/projects/bim/${projectId}/contract/edit`);

            return;
        }

        if (!projectData.contractNavigation?.data?.contractNavData?.metadata?.hasObjects) {
            navigate(`/projects/bim/${projectId}/contract/structure`);
        }
    }, [
        hasFilledAddresses,
        hasFilledMandatoryProperties,
        projectData?.contractNavigation?.data?.contractNavData?.metadata?.hasObjects,
        navigate,
        projectId,
    ]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn,
        });

    const onCloseConfirmation = useCallback(() => {
        setTimeout(() => handleCloseConfirmationDialog());
    }, [handleCloseConfirmationDialog]);

    useEffect(() => {
        resetData(undefined);
    }, [resetData]);

    useEffect(() => {
        const createdBimOrderId = createBimOrderData?.bimCreateWorkOrder?.data?.id;

        if (createdBimOrderId && createBimOrderStatus === RequestStatus.SUCCESS) {
            if (isFrameContract) {
                navigate(`/projects/bim/${projectId}/order/${createdBimOrderId}/structure`);
            } else {
                navigate(`/projects/bim/${projectId}/order/${createdBimOrderId}`);
            }

            resetData(undefined);
            refreshProject();
        }
    }, [
        createBimOrderData?.bimCreateWorkOrder?.data?.id,
        createBimOrderStatus,
        isFrameContract,
        navigate,
        projectId,
        refreshProject,
        resetData,
    ]);

    const createBimOrderHandler = useCallback(() => {
        if (
            projectData?.contractNavigation?.data?.contractNavData?.metadata?.hasObjects &&
            hasFilledAddresses &&
            hasFilledMandatoryProperties
        ) {
            resetData(undefined);
            dispatch(createBimOrderRequest(createBimOrderVariables));

            sendGpnAnalyticsEvent({
                componentId: 'rim_create order button_pressed',
                ...analyticsData,
            });
        } else {
            handleOpenConfirmationDialog();
        }
    }, [
        analyticsData,
        createBimOrderRequest,
        createBimOrderVariables,
        dispatch,
        handleOpenConfirmationDialog,
        hasFilledAddresses,
        hasFilledMandatoryProperties,
        projectData?.contractNavigation?.data?.contractNavData?.metadata?.hasObjects,
        resetData,
        sendGpnAnalyticsEvent,
    ]);

    const textDialog = useMemo(() => {
        if (!hasFilledAddresses) {
            return "Для создания наряд-заказа необходимо заполнить поля договора 'Адрес заказчика' и 'Адрес подрядчика'";
        }

        if (!hasFilledMandatoryProperties) {
            return 'Заполните обязательные поля карточки Договора для создания Наряд-Заказа';
        }

        if (!projectData?.contractNavigation?.data?.contractNavData?.metadata?.hasObjects) {
            return "Необходимо заполнить страницу 'Структура Договора'";
        }

        return '';
    }, [
        hasFilledAddresses,
        hasFilledMandatoryProperties,
        projectData?.contractNavigation?.data?.contractNavData?.metadata?.hasObjects,
    ]);

    const CreateOrderConfirmationDialog = useMemo(
        () => (
            <ConfirmationDialog
                textHeader={orderCreateTitle}
                textConfirmation={textDialog}
                cancelButtonText="Отменить"
                confirmButtonText="Заполнить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={onCloseConfirmation}
                preventButtonsDefaults
                styleError
            />
        ),
        [handleActionQuery, isOpenConfirmationDialog, onCloseConfirmation, orderCreateTitle, textDialog],
    );

    return {
        orderCreateBtnTitle,
        createBimOrderHandler,
        CreateOrderConfirmationDialog,
        isOrderCreateDisabled,
    };
};
